import { FunctionComponent, ReactNode, useEffect, useRef } from "react";
import { Dialog, DialogActions, DialogContent } from "@mui/material";

import { useTranslation } from "react-i18next";
import { Stack } from "@mui/system";

import { classNames } from "@/utils/classNames";

import { Button } from "../Button/Button";
import { ButtonColor } from "../Button/types";
import { Checkbox } from "../Checkbox/Checkbox";

// A trigger's second click lands on its dialog's backdrop; ignore that click for a moment (PREC-5042).
const BACKDROP_CLICK_GRACE_MS = 500;

interface ModalProps {
	children: ReactNode;
	title: string;
	icon?: JSX.Element;
	titleAlign?: "left" | "center" | "right";
	titleSize?: "sm" | "md" | "lg";
	titleWeight?: "normal" | "bold";
	subtitle?: string;
	isOpened: boolean;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	handleClose?: (e?: any, reason?: "escapeKeyDown" | "backdropClick") => void;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	handleClear?: (e?: any) => void;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	handleSave?: (e?: any) => void;
	handleThirdSave?: () => void;
	handleSecondSave?: () => void;
	secondSubmitButtonText?: string;
	thirdSubmitButtonText?: string;
	isLoading?: boolean;
	submitButtonText?: string;
	submitButtonColor?: string;
	secondSubmitButtonColor?: string;
	clearButtonText?: string;
	clearButtonColor?: string;
	cancelButtonText?: string;
	size?: "xs" | "sm" | "md" | "lg" | "xl";
	overflow?: boolean;
	checkbox?: boolean;
	handleCheckboxClick?: () => void;
	checkboxLabel?: string;
	titleFlag?: string;
	disableSave?: boolean;
	disableSecondSave?: boolean;
	disableThirdSave?: boolean;
	zIndex?: number;
	/**
	 * Move focus into the dialog and keep it there, so Escape and the keyboard reach it. Off by
	 * default (popovers inside some modals need focus outside them); a short yes/no question that
	 * guards a send turns it on (PREC-4312).
	 */
	takeFocus?: boolean;
	footer?: ReactNode;
	dialogContentSx?: object;
}

export const Modal: FunctionComponent<ModalProps> = ({
	title,
	titleAlign = "left",
	titleSize = "md",
	titleWeight = "normal",
	subtitle,
	children,
	isOpened,
	handleClose,
	handleSave,
	handleSecondSave,
	handleThirdSave,
	checkbox,
	handleCheckboxClick,
	secondSubmitButtonText,
	thirdSubmitButtonText,
	submitButtonText,
	submitButtonColor,
	secondSubmitButtonColor,
	isLoading = false,
	size = "xs",
	handleClear,
	clearButtonText,
	clearButtonColor,
	overflow,
	checkboxLabel,
	dialogContentSx,
	cancelButtonText,
	disableSave,
	disableSecondSave,
	disableThirdSave,
	zIndex = 1000,
	takeFocus = false,
	icon,
	titleFlag,
	footer,
}) => {
	const { t } = useTranslation();

	const openedAtRef = useRef<number | null>(null);

	useEffect(() => {
		// The dialog is keepMounted, so its mount time is not when it opened; record each
		// transition of isOpened to true instead.
		openedAtRef.current = isOpened ? Date.now() : null;
	}, [isOpened]);

	// Escape is always a dismiss; only a backdrop click inside the opening window is ignored.
	const handleDialogClose = (_event: object, reason: string) => {
		if (
			reason === "backdropClick" &&
			openedAtRef.current !== null &&
			Date.now() - openedAtRef.current < BACKDROP_CLICK_GRACE_MS
		) {
			return;
		}

		// The caller needs the reason: a global-search dismiss on Escape goes back one level
		// instead of closing (GlobalSearchModal), so it must reach handleClose.
		handleClose?.(_event, reason as "escapeKeyDown" | "backdropClick");
	};

	const cssForOverflow = overflow
		? {
				"& .MuiDialogContent-root": {
					overflow: "visible",
				},
			}
		: {};

	return (
		<Dialog
			disableAutoFocus={!takeFocus}
			disableEnforceFocus={!takeFocus}
			fullWidth
			keepMounted
			maxWidth={size}
			open={isOpened}
			// When a modal needs popovers to spill out (overflow), its DialogContent is
			// `overflow: visible` and can't scroll. Pair that with `scroll="body"` so the
			// dialog container scrolls instead — tall content stays reachable while the
			// popover still escapes. Short modals remain vertically centered.
			scroll={overflow ? "body" : "paper"}
			sx={{
				overflow: "visible!important",
				"& .MuiDialog-paper": {
					borderRadius: "2xl",
					overflow: "visible",
				},
				"& .MuiDialogContent-root": {
					overflowY: "auto",
				},
				...cssForOverflow,
				zIndex: zIndex,
			}}
			onAbort={handleClose}
			// Without a handleClose, dismissing (Escape / backdrop) must do
			// nothing — it must never fall through to handleSave.
			onClose={handleClose ? handleDialogClose : undefined}
		>
			<div
				className={classNames(
					"text-md mx-6 mt-6 mb-1",
					titleAlign === "center" && "text-center",
					titleSize === "sm" && "text-sm",
					titleSize === "md" && "text-md",
					titleSize === "lg" && "text-lg",
					titleWeight === "bold" && "font-bold",
					icon ? "flex items-center gap-2" : null,
					titleFlag ? "flex gap-3 " : null
				)}
			>
				{icon ? icon : null}
				{title}
				{titleFlag ? (
					<span className="text-xs self-center uppercase font-medium px-3 py-1 rounded-full bg-blue-50 text-blue-600 border border-blue-100 tracking-wide">
						{titleFlag}
					</span>
				) : null}
			</div>
			<div className={classNames("text-sm mx-6 mt-1 mb-4")}>{subtitle ?? ""}</div>

			<DialogContent
				sx={{ pt: 2, pb: 4, height: "fit-content", overflow: "auto", ...dialogContentSx }}
			>
				{children}
			</DialogContent>

			{footer ? (
				<div className="border-t border-gray-300 px-5 py-3 flex items-center justify-between">
					{footer}
				</div>
			) : (
				<DialogActions sx={{ px: 3, py: 2, overflow: "visible" }}>
					{handleCheckboxClick && typeof checkbox === "boolean" && (
						<div className="grow w-full flex items-center">
							<Checkbox isChecked={checkbox} onChange={handleCheckboxClick}>
								{checkboxLabel}
							</Checkbox>
						</div>
					)}
					<div
						className={classNames(
							"w-full flex",
							handleClear && handleSave ? "justify-between" : "justify-end"
						)}
					>
						{handleClear && (
							<div className="min-w-[150px] w-fit mr-2">
								<Button
									color={
										clearButtonColor
											? (clearButtonColor as ButtonColor)
											: ButtonColor.DANGER_SECONDARY
									}
									title={clearButtonText ?? t("basics.clear")}
									onClick={handleClear}
								/>
							</div>
						)}
						<Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
							{/* LEFT SIDE */}
							<Stack direction="row" spacing={2}>
								{handleClose && (
									<div className="w-fit">
										<Button
											color={ButtonColor.ACTION_SECONDARY}
											title={cancelButtonText ?? t("basics.cancel")}
											onClick={handleClose}
										/>
									</div>
								)}
							</Stack>

							{/* RIGHT SIDE */}
							<Stack direction="row" spacing={2}>
								{handleSecondSave && (
									<div className="w-fit">
										<Button
											color={
												secondSubmitButtonColor
													? (secondSubmitButtonColor as ButtonColor)
													: ButtonColor.ACTION_SECONDARY
											}
											disabled={disableSecondSave}
											isLoading={isLoading}
											title={secondSubmitButtonText ?? t("basics.saveChanges")}
											onClick={handleSecondSave}
										/>
									</div>
								)}

								{handleThirdSave && (
									<div className="w-fit">
										<Button
											color={ButtonColor.ACTION_SECONDARY}
											disabled={disableThirdSave}
											isLoading={isLoading}
											title={thirdSubmitButtonText ?? t("basics.saveChanges")}
											onClick={handleThirdSave}
										/>
									</div>
								)}

								{handleSave && (
									<div className="w-fit">
										<Button
											color={
												submitButtonColor ? (submitButtonColor as ButtonColor) : ButtonColor.BRAND
											}
											disabled={isLoading || disableSave}
											isLoading={isLoading}
											title={submitButtonText ?? t("basics.saveChanges")}
											onClick={handleSave}
										/>
									</div>
								)}
							</Stack>
						</Stack>{" "}
					</div>
				</DialogActions>
			)}
		</Dialog>
	);
};
